import enhancedApiClient from '../../../services/enhancedApiClient';
import { Stage, CreateStageRequest, UpdateStageRequest, StageFilters } from '../types/stage';

class StageService {
  private baseUrl = '/stages';

  /**
   * Get all stages with optional filters
   */
  async getAllStages(filters: StageFilters = {}): Promise<{ success: boolean; data: Stage[]; error?: string }> {
    try {
      const queryParams = new URLSearchParams();
      
      if (filters.category) queryParams.append('category', filters.category);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.parent_stage_id !== undefined) {
        queryParams.append('parent_stage_id', filters.parent_stage_id === null ? 'null' : filters.parent_stage_id);
      }
      if (filters.search) queryParams.append('search', filters.search);

      const url = queryParams.toString() ? `${this.baseUrl}?${queryParams.toString()}` : this.baseUrl;
      const response = await enhancedApiClient.get(url);
      console.log("response data ===", response)

      if (response.success && response.data) {
        return {
          success: true,
          data: response.data
        };
      } else {
        const errorMessage = response.error?.message || 
                            (typeof response.error === 'string' ? response.error : 'Failed to fetch stages');
        return {
          success: false,
          data: [],
          error: errorMessage
        };
      }
    } catch (error: any) {
      console.error('Error fetching stages:', error);
      const errorMessage = error.response?.data?.error?.message || 
                          error.response?.data?.error ||
                          (typeof error.response?.data?.error === 'string' ? error.response.data.error : null) ||
                          error.message || 
                          'Failed to fetch stages';
      return {
        success: false,
        data: [],
        error: errorMessage
      };
    }
  }

  /**
   * Get a single stage by ID
   */
  async getStageById(id: string): Promise<{ success: boolean; data?: Stage; error?: string }> {
    try {
      const response = await enhancedApiClient.get(`${this.baseUrl}/${id}`);
      if (response.success && response.data) {
        return {
          success: true,
          data: response.data
        };
      } else {
        const errorMessage = response.error?.message || 
                            (typeof response.error === 'string' ? response.error : 'Failed to fetch stage');
        return {
          success: false,
          error: errorMessage
        };
      }
    } catch (error: any) {
      console.error('Error fetching stage:', error);
      const errorMessage = error.response?.data?.error?.message || 
                          error.response?.data?.error ||
                          (typeof error.response?.data?.error === 'string' ? error.response.data.error : null) ||
                          error.message || 
                          'Failed to fetch stage';
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Create a new stage
   */
  async createStage(stageData: CreateStageRequest): Promise<{ success: boolean; data?: Stage; error?: string }> {
    try {
      console.log('Creating stage with data:', stageData);
      const response = await enhancedApiClient.post(this.baseUrl, stageData);
      console.log('Stage creation response:', response);
      
      // enhancedApiClient.post returns ApiResponse with { success, data?, error? }
      if (response.success && response.data) {
        return {
          success: true,
          data: response.data
        };
      } else {
        // Extract error message from error object
        const errorMessage = response.error?.message || 
                            (typeof response.error === 'string' ? response.error : 'Failed to create stage');
        return {
          success: false,
          error: errorMessage
        };
      }
    } catch (error: any) {
      console.error('Error creating stage:', error);
      // Handle axios errors
      const errorMessage = error.response?.data?.error?.message || 
                          error.response?.data?.error ||
                          (typeof error.response?.data?.error === 'string' ? error.response.data.error : null) ||
                          error.message || 
                          'Failed to create stage';
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Update an existing stage
   */
  async updateStage(id: string, stageData: Partial<CreateStageRequest>): Promise<{ success: boolean; data?: Stage; error?: string }> {
    try {
      const response = await enhancedApiClient.put(`${this.baseUrl}/${id}`, stageData);
      if (response.success && response.data) {
        return {
          success: true,
          data: response.data
        };
      } else {
        const errorMessage = response.error?.message || 
                            (typeof response.error === 'string' ? response.error : 'Failed to update stage');
        return {
          success: false,
          error: errorMessage
        };
      }
    } catch (error: any) {
      console.error('Error updating stage:', error);
      const errorMessage = error.response?.data?.error?.message || 
                          error.response?.data?.error ||
                          (typeof error.response?.data?.error === 'string' ? error.response.data.error : null) ||
                          error.message || 
                          'Failed to update stage';
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Delete a stage (soft delete by setting status to inactive)
   */
  async deleteStage(id: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await enhancedApiClient.delete(`${this.baseUrl}/${id}`);
      if (response.success) {
        return {
          success: true
        };
      } else {
        const errorMessage = response.error?.message || 
                            (typeof response.error === 'string' ? response.error : 'Failed to delete stage');
        return {
          success: false,
          error: errorMessage
        };
      }
    } catch (error: any) {
      console.error('Error deleting stage:', error);
      const errorMessage = error.response?.data?.error?.message || 
                          error.response?.data?.error ||
                          (typeof error.response?.data?.error === 'string' ? error.response.data.error : null) ||
                          error.message || 
                          'Failed to delete stage';
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Get stages organized in a tree structure (parent-child relationships)
   */
  async getStagesTree(): Promise<{ success: boolean; data: Stage[]; error?: string }> {
    try {
      const result = await this.getAllStages();
      if (!result.success) {
        return result;
      }

      // Organize stages into tree structure
      const stages = result.data;
      const stageMap = new Map<string, Stage>();
      const rootStages: Stage[] = [];

      // Create a map of all stages
      stages.forEach(stage => {
        stageMap.set(stage.id, { ...stage, child_stages: [] });
      });

      // Build the tree
      stages.forEach(stage => {
        const stageWithChildren = stageMap.get(stage.id)!;
        
        if (stage.parent_stage_id) {
          const parent = stageMap.get(stage.parent_stage_id);
          if (parent) {
            parent.child_stages!.push(stageWithChildren);
          }
        } else {
          rootStages.push(stageWithChildren);
        }
      });

      return {
        success: true,
        data: rootStages
      };
    } catch (error: any) {
      console.error('Error building stages tree:', error);
      return {
        success: false,
        data: [],
        error: error.message || 'Failed to build stages tree'
      };
    }
  }

  /**
   * Get only root stages (stages without parent)
   */
  async getRootStages(): Promise<{ success: boolean; data: Stage[]; error?: string }> {
    return this.getAllStages({ parent_stage_id: null });
  }

  /**
   * Get child stages of a specific parent stage
   */
  async getChildStages(parentId: string): Promise<{ success: boolean; data: Stage[]; error?: string }> {
    return this.getAllStages({ parent_stage_id: parentId });
  }

  /**
   * Search stages by name or description
   */
  async searchStages(searchTerm: string): Promise<{ success: boolean; data: Stage[]; error?: string }> {
    return this.getAllStages({ search: searchTerm });
  }
}

export default new StageService();
